import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { CloseOutlined, SaveOutlined } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";

import { useCurrentUserRoleNames } from "components/RequirePermission/useAccessPermissions";
import { useCreateRoleAssignmentMutation } from "state/rtkQuery/access/accessMutations";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useLazyGetAdCheckEmailQuery, useLazyGetUserQuery } from "state/rtkQuery/user/userQueries";
import { extractApiErrorMessage } from "utils/accessManagement/extractApiErrorMessage";
import { useAccessPlatformId } from "utils/accessManagement/useAccessPlatformId";
import { formatRoleLabel } from "utils/accessManagement/roleDisplay";
import {
  buildCreateAssignmentPayload,
  buildEmptyAssignmentForm,
  canManageRole,
  getAlreadyAssignedOrgIds,
  getMyMaxRolePriority,
  isOrgExemptRole,
  isValidEmail,
  validateAssignmentForm,
} from "utils/accessManagement/roleAssignmentForm";
import { showToast } from "utils/reusableToast";
import { useDebounce } from "utils/useDebounce";

import OrgCheckboxList from "./OrgCheckboxList";

import "pages/ecrOps/OrgEditDrawer.css";

// Right-side drawer for POST /role-assignments. Target type (User/Application)
// is a segmented control that swaps the visible field set, per spec.
const AssignRoleDrawer = ({ open, onClose, roles, onCreated, initialEmail, initialRoleId, existingAssignments }) => {
  const currentUserEmail = useSelector((state) => state.auth?.userAccount?.username || "");
  const myRoleNames = useCurrentUserRoleNames();
  const [form, setForm] = useState(() =>
    buildEmptyAssignmentForm({ assignedBy: currentUserEmail, email: initialEmail, roleId: initialRoleId })
  );
  const [errors, setErrors] = useState({});
  const [createAssignment, { isLoading, isError, error, reset }] = useCreateRoleAssignmentMutation();
  // Only this platform's organizations are offered: the platform picked in the
  // ECR Ops selector, falling back to the default platform until that resolves
  // (same rule as the Org Management page). Nothing is fetched until a platform
  // id is known, so the list can never briefly show every platform's orgs.
  const { platformId, isLoadingPlatform } = useAccessPlatformId();
  const { data: activeOrgs, isFetching: isFetchingOrgs } = useGetActiveOrganizationsQuery({ platformId }, { skip: !platformId });
  const isLoadingOrgs = isLoadingPlatform || isFetchingOrgs;

  // Email suggestions as the admin types — debounced, and only once there's
  // enough to search on (matching UserOrgSearch's own 3-char minimum). The
  // /users?email= endpoint is normally used for an exact lookup elsewhere in
  // this app; there's no confirmed partial-match contract for it, so this
  // is deliberately defensive — if the backend only ever returns an exact
  // match (or nothing) for a partial string, the list just stays empty
  // until a full email is typed, which still works fine via freeSolo.
  // Directory (AD) check — the same GET /users/ad/check-email the org settings
  // "add approver" box uses. It runs when you leave the email box and again on
  // submit (the second call is served from cache), so a typo or someone who
  // isn't in the directory is caught before a role is assigned to them.
  const [triggerAdCheck] = useLazyGetAdCheckEmailQuery();
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const verifyEmail = async () => {
    const email = form.email.trim();
    if (!email || !isValidEmail(email)) return true; // empty/format problems are reported by the form validation
    setIsVerifyingEmail(true);
    try {
      const data = await triggerAdCheck(email.toLowerCase(), true).unwrap();
      if (data?.exists_in_ad) return true;
      setErrors((e) => ({ ...e, email: "No user with this email was found in the directory." }));
    } catch {
      setErrors((e) => ({ ...e, email: "Couldn't verify this email right now. Please try again." }));
    } finally {
      setIsVerifyingEmail(false);
    }
    return false;
  };

  const [triggerUserSearch, { data: userSuggestions, isFetching: isSearchingEmail }] = useLazyGetUserQuery();
  const debouncedEmailInput = useDebounce(form.email, 300);
  useEffect(() => {
    if (debouncedEmailInput?.trim().length >= 3) {
      triggerUserSearch({ email: debouncedEmailInput.trim() });
    }
  }, [debouncedEmailInput]); // eslint-disable-line
  const emailOptions = Array.isArray(userSuggestions) ? userSuggestions : [];
  // The debounce delays the search itself, so there's a window right after
  // typing where a search hasn't fired yet either — show the spinner then
  // too, not just while isFetching is true, so feedback appears immediately
  // rather than only after the debounce settles.
  const isEmailSearchPending =
    isSearchingEmail || (form.email.trim().length >= 3 && form.email !== debouncedEmailInput);

  useEffect(() => {
    if (open) {
      setForm(buildEmptyAssignmentForm({ assignedBy: currentUserEmail, email: initialEmail, roleId: initialRoleId }));
      setErrors({});
      reset();
    }
  }, [open]);

  const set = (field) => (value) => {
    setForm((f) => {
      // EnterpriseAdmin/OperationsAdmin are cross-org — clear any previously
      // selected orgs so a stale org_id can't be silently sent for them.
      const next = { ...f, [field]: value };
      if (field === "roleId") {
        const nextRole = roles.find((r) => r.id === value);
        if (isOrgExemptRole(nextRole?.role_name)) next.orgIds = [];
      }
      return next;
    });
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const setFromEvent = (field) => (e) => set(field)(e.target.value);

  const activeRoles = roles.filter((r) => r.is_active !== false);
  const inactiveRoles = roles.filter((r) => r.is_active === false);
  const selectedRole = roles.find((r) => r.id === form.roleId) || null;
  const selectedRoleName = selectedRole?.role_name;
  const isOrgExempt = isOrgExemptRole(selectedRoleName);
  // Orgs this user already holds the chosen role in: shown ticked + locked, and
  // left out of the POST (picks are only the new ones).
  const lockedOrgIds = useMemo(
    () => (isOrgExempt ? [] : getAlreadyAssignedOrgIds(existingAssignments, { roleId: form.roleId, email: form.email, userId: form.userId })),
    [existingAssignments, form.roleId, form.email, form.userId, isOrgExempt]
  );

  // Role hierarchy: you can only assign a role at or below your own
  // highest-ranked role (by each role's backend-defined `priority`). Higher
  // roles still appear in the list (so their existence isn't hidden), just
  // disabled with a tooltip explaining why — see getMyMaxRolePriority's own
  // comment for why this uses priority rather than hardcoded role names.
  const myMaxPriority = getMyMaxRolePriority(myRoleNames, roles);

  const handleSubmit = async () => {
    const validationErrors = validateAssignmentForm(form, { selectedRoleName });
    if (selectedRole && !canManageRole(selectedRole, myMaxPriority)) {
      validationErrors.roleId = "You cannot assign a role higher than your own.";
    }
    if (validationErrors.orgIds && lockedOrgIds.length > 0) {
      validationErrors.orgIds = "Select at least one organization this user doesn't already have.";
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (form.targetType === "user" && !(await verifyEmail())) return;

    try {
      await createAssignment(buildCreateAssignmentPayload(form, { selectedRoleName, platformId })).unwrap();
      showToast({ title: "Role assigned", description: "The assignment was created successfully.", type: "success" });
      onCreated();
    } catch (err) {
      // Server-side validation errors render inline below; preserve entered values.
      const fieldErrors = err?.data?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        // The API names the field org_id; the form's own key is orgIds.
        setErrors((e) => ({ ...e, ...fieldErrors, ...(fieldErrors.org_id ? { orgIds: fieldErrors.org_id } : {}) }));
      }
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}>
      <div className="org-drawer__header">
        <div className="org-drawer__header-text">
          <p className="org-drawer__header-eyebrow">Access Management</p>
          <h2 className="org-drawer__header-title">Assign role</h2>
        </div>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ color: "#9ca3af" }}>
            <CloseOutlined />
          </IconButton>
        </Tooltip>
      </div>

      <div className="org-drawer__body">
        <div className="org-drawer__section">
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={form.targetType}
            onChange={(e, value) => value && set("targetType")(value)}
            aria-label="Assignment target type"
          >
            <ToggleButton value="user">User</ToggleButton>
            <ToggleButton value="application">Application</ToggleButton>
          </ToggleButtonGroup>
        </div>

        <Divider />

        <div className="org-drawer__section">
          <p className="org-drawer__section-title">Role</p>
          <div className="org-drawer__fields">
            <Autocomplete
              options={[...activeRoles, ...inactiveRoles]}
              getOptionLabel={(role) => formatRoleLabel(role.role_name)}
              getOptionDisabled={(role) => role.is_active === false || !canManageRole(role, myMaxPriority)}
              value={selectedRole}
              onChange={(e, role) => set("roleId")(role?.id || "")}
              renderOption={(props, role) => {
                const isTooHighRank = role.is_active !== false && !canManageRole(role, myMaxPriority);
                return (
                  <li {...props} key={role.id} title={isTooHighRank ? "You cannot assign a role higher than your own." : undefined}>
                    <Box>
                      <Typography variant="body2">
                        {formatRoleLabel(role.role_name)}
                        {role.is_active === false && " (inactive)"}
                        {isTooHighRank && " (higher than your role)"}
                      </Typography>
                      {role.description && (
                        <Typography variant="caption" color="text.secondary">
                          {role.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {(role.permissions || []).length} permissions
                        {role.priority != null ? ` · priority ${role.priority}` : ""}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Role"
                  required
                  size="small"
                  error={!!errors.roleId}
                  helperText={errors.roleId}
                />
              )}
            />
          </div>
        </div>

        <Divider />

        <div className="org-drawer__section">
          <p className="org-drawer__section-title">Identity</p>
          <div className="org-drawer__fields">
            {form.targetType === "user" ? (
              <>
                <TextField
                  label="User ID"
                  size="small"
                  fullWidth
                  value={form.userId}
                  onChange={setFromEvent("userId")}
                  error={!!errors.userId}
                />
                <Autocomplete
                  freeSolo
                  options={emailOptions}
                  filterOptions={(options) => options}
                  getOptionLabel={(option) => (typeof option === "string" ? option : option.email || "")}
                  inputValue={form.email}
                  onInputChange={(e, value) => set("email")(value)}
                  onChange={(e, option) => {
                    if (option && typeof option !== "string") {
                      setForm((f) => ({ ...f, email: option.email || "", userId: option._id || f.userId }));
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      <Box>
                        <Typography variant="body2">{option.email}</Typography>
                        {(option.full_name || option.last_name) && (
                          <Typography variant="caption" color="text.secondary">
                            {option.full_name || `${option.last_name}, ${option.first_name || ""}`.trim()}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Email"
                      size="small"
                      fullWidth
                      onBlur={() => !errors.email && verifyEmail()}
                      error={!!errors.email}
                      helperText={errors.email || errors.userId || "Provide a user ID or an email — one is enough."}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(isEmailSearchPending || isVerifyingEmail) && <CircularProgress size={16} sx={{ color: "#86BC25", mr: 1 }} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </>
            ) : (
              <TextField
                label="Application ID"
                size="small"
                fullWidth
                value={form.appId}
                onChange={setFromEvent("appId")}
                helperText="Recommended for application assignments."
              />
            )}

            {!isOrgExempt && (
              <OrgCheckboxList
                orgs={activeOrgs || []}
                selectedIds={form.orgIds}
                lockedIds={lockedOrgIds}
                onChange={set("orgIds")}
                isLoading={isLoadingOrgs && !activeOrgs}
                required
                error={errors.orgIds}
                helperText="Required for this role. Pick every organization this role applies to."
              />
            )}
          </div>
        </div>

        <Divider />

        <div className="org-drawer__section">
          <p className="org-drawer__section-title">Assignment</p>
          <div className="org-drawer__fields">
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => set("isActive")(e.target.checked)} size="small" />}
              label={form.isActive ? "Active" : "Inactive"}
            />
          </div>
        </div>

        {isError && (
          <div className="org-drawer__section">
            <Alert severity="error" sx={{ fontSize: "13px" }}>
              {extractApiErrorMessage(error, "Failed to create role assignment. Please try again.")}
            </Alert>
          </div>
        )}
      </div>

      <div className="org-drawer__footer">
        <button type="button" className="org-drawer__btn org-drawer__btn--cancel" onClick={onClose} disabled={isLoading}>
          Cancel
        </button>
        <button type="button" className="org-drawer__btn org-drawer__btn--save" onClick={handleSubmit} disabled={isLoading || isVerifyingEmail}>
          {isLoading ? <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> : <SaveOutlined style={{ fontSize: 16, marginRight: 6 }} />}
          {isLoading ? "Assigning…" : "Assign role"}
        </button>
      </div>
    </Drawer>
  );
};

AssignRoleDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
  onCreated: PropTypes.func.isRequired,
  initialEmail: PropTypes.string,
  initialRoleId: PropTypes.string,
};

export default AssignRoleDrawer;
